/**
 * Verrou de veille d'écran — EF-MOD-008.
 *
 * Le réglage est désactivé par défaut en raison de son effet sur la batterie.
 * L'application affiche l'état réel et n'affirme jamais que l'écran restera
 * allumé lorsque le verrou n'est pas actif.
 */

export type WakeLockStatus = 'actif' | 'libéré' | 'refusé' | 'non pris en charge';

interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

function wakeLockApi(): WakeLockLike | undefined {
  const navigatorWithWakeLock = globalThis.navigator as Navigator & { wakeLock?: WakeLockLike };
  return navigatorWithWakeLock?.wakeLock;
}

export class ScreenWakeLock {
  private sentinel: WakeLockSentinelLike | null = null;
  private status: WakeLockStatus = wakeLockApi() === undefined ? 'non pris en charge' : 'libéré';

  constructor(private readonly onStatusChange: (status: WakeLockStatus) => void) {}

  get current(): WakeLockStatus {
    return this.status;
  }

  static get isSupported(): boolean {
    return wakeLockApi() !== undefined;
  }

  private setStatus(status: WakeLockStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.onStatusChange(status);
  }

  /** Demande le verrou. L'état affiché est toujours l'état réellement obtenu. */
  async request(): Promise<WakeLockStatus> {
    const api = wakeLockApi();
    if (api === undefined) {
      this.setStatus('non pris en charge');
      return this.status;
    }
    if (this.sentinel !== null && !this.sentinel.released) {
      this.setStatus('actif');
      return this.status;
    }

    try {
      const sentinel = await api.request('screen');
      this.sentinel = sentinel;
      sentinel.addEventListener('release', () => {
        this.sentinel = null;
        this.setStatus('libéré');
      });
      this.setStatus('actif');
    } catch {
      // Un refus ne bloque pas le mode course.
      this.sentinel = null;
      this.setStatus('refusé');
    }
    return this.status;
  }

  /** Le verrou est considéré comme libéré au passage à l'arrière-plan. */
  async release(): Promise<void> {
    const sentinel = this.sentinel;
    this.sentinel = null;
    if (this.status !== 'non pris en charge') this.setStatus('libéré');
    if (sentinel === null || sentinel.released) return;
    try {
      await sentinel.release();
    } catch {
      // Le verrou a déjà été repris par le système : rien à signaler.
    }
  }

  /**
   * Redemande le verrou au retour à l'état visible lorsque le réglage est
   * toujours activé.
   */
  async reacquireIfEnabled(enabled: boolean): Promise<void> {
    if (!enabled) return;
    await this.request();
  }
}
